/**
 * Finance AR / overdue server-side query (L2-UI-FN-01 · L2-UI-FN-02)
 *
 * Filters `finance_state` by receivable account codes, balance, dueDate, and overdue markers.
 */

import {
  getCustomFieldValue,
  getFinanceAssetValue,
  getFinanceStateInfo,
} from '@domain/finance/types/FinanceTypes';
import { DEFAULT_ACCOUNT_CODES } from '@shared/finance/standard-chart-of-accounts';
import type { UniversalBaseEntity } from '@shared/types/entity';

export const RECEIVABLE_ACCOUNT_CODES = new Set<string>([
  DEFAULT_ACCOUNT_CODES.ACCOUNTS_RECEIVABLE,
  DEFAULT_ACCOUNT_CODES.TRADE_RECEIVABLES,
  DEFAULT_ACCOUNT_CODES.NOTES_RECEIVABLE,
]);

const DUE_DATE_FIELD_KEYS = ['dueDate', 'due_date', 'paymentDueDate', 'maturityDate'] as const;

export type AgingBucket = 'current' | '1-30' | '31-60' | '61-90' | '90+';

export type AgingBucketSummary = Record<AgingBucket, number>;

export interface FinanceReceivablesListResult {
  items: UniversalBaseEntity[];
  agingSummary: AgingBucketSummary;
  total: number;
}

export interface FinanceOverdueListResult {
  items: UniversalBaseEntity[];
  total: number;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDateValue(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return null;
}

function resolveAsOfDate(now: unknown): Date {
  if (now instanceof Date && !Number.isNaN(now.getTime())) {
    return now;
  }
  return new Date();
}

export function isReceivableAccountCode(accountCode: string | undefined): boolean {
  if (!accountCode) {
    return false;
  }
  return RECEIVABLE_ACCOUNT_CODES.has(accountCode);
}

export function readFinanceStateDueDate(record: UniversalBaseEntity): Date | null {
  for (const key of DUE_DATE_FIELD_KEYS) {
    const parsed = parseDateValue(getCustomFieldValue(record, key));
    if (parsed) {
      return parsed;
    }
  }
  return null;
}

export function isReceivableFinanceState(record: UniversalBaseEntity): boolean {
  const info = getFinanceStateInfo(record);
  if (!isReceivableAccountCode(info?.accountCode)) {
    return false;
  }
  return getFinanceAssetValue(record) > 0;
}

export function computeAgingDays(record: UniversalBaseEntity, now?: Date): number | null {
  const asOf = resolveAsOfDate(now);
  const dueDate = readFinanceStateDueDate(record);
  if (!dueDate) {
    return null;
  }
  const diffMs = startOfDay(asOf).getTime() - startOfDay(dueDate).getTime();
  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
}

export function resolveAgingBucket(days: number | null): AgingBucket {
  if (days === null || days <= 0) {
    return 'current';
  }
  if (days <= 30) {
    return '1-30';
  }
  if (days <= 60) {
    return '31-60';
  }
  if (days <= 90) {
    return '61-90';
  }
  return '90+';
}

function readOverdueFlag(record: UniversalBaseEntity): boolean {
  const raw = getCustomFieldValue(record, 'overdue') ?? getCustomFieldValue(record, 'isOverdue');
  return raw === true || raw === 'true' || raw === 'yes' || raw === 1;
}

export function isOverdueFinanceState(record: UniversalBaseEntity, now?: Date): boolean {
  const asOf = resolveAsOfDate(now);
  if (!isReceivableFinanceState(record)) {
    return false;
  }
  if (readOverdueFlag(record)) {
    return true;
  }
  const dueDate = readFinanceStateDueDate(record);
  if (!dueDate) {
    return false;
  }
  return startOfDay(dueDate) < startOfDay(asOf);
}

export function filterReceivableFinanceStates(rows: UniversalBaseEntity[]): UniversalBaseEntity[] {
  return rows.filter(isReceivableFinanceState);
}

export function filterOverdueFinanceStates(
  rows: UniversalBaseEntity[],
  now?: Date
): UniversalBaseEntity[] {
  const asOf = resolveAsOfDate(now);
  return rows.filter((row) => isOverdueFinanceState(row, asOf));
}

export function summarizeAgingBuckets(rows: UniversalBaseEntity[], now?: Date): AgingBucketSummary {
  const asOf = resolveAsOfDate(now);
  const summary: AgingBucketSummary = {
    current: 0,
    '1-30': 0,
    '31-60': 0,
    '61-90': 0,
    '90+': 0,
  };

  for (const row of rows) {
    const bucket = resolveAgingBucket(computeAgingDays(row, asOf));
    summary[bucket] += 1;
  }

  return summary;
}

export const AGING_BUCKET_LABELS: Record<AgingBucket, string> = {
  current: '미연체',
  '1-30': '1–30일',
  '31-60': '31–60일',
  '61-90': '61–90일',
  '90+': '90일+',
};

export function buildFinanceReceivablesList(
  rows: UniversalBaseEntity[],
  now?: Date
): FinanceReceivablesListResult {
  const items = filterReceivableFinanceStates(rows);
  return {
    items,
    agingSummary: summarizeAgingBuckets(items, now),
    total: items.length,
  };
}

export function buildFinanceOverdueList(
  rows: UniversalBaseEntity[],
  now?: Date
): FinanceOverdueListResult {
  const items = filterOverdueFinanceStates(rows, now);
  return {
    items,
    total: items.length,
  };
}

/** Count receivable rows whose aging days meet or exceed the threshold (role queue ar-aging). */
export function countReceivablesWithMinAgingDays(
  rows: UniversalBaseEntity[],
  minAgingDays: number,
  now?: Date
): number {
  if (minAgingDays <= 0) {
    return filterReceivableFinanceStates(rows).length;
  }
  const asOf = resolveAsOfDate(now);
  return filterReceivableFinanceStates(rows).filter((row) => {
    const agingDays = computeAgingDays(row, asOf);
    return agingDays !== null && agingDays >= minAgingDays;
  }).length;
}

/** Count overdue receivable rows (role queue overdue-ar — past due date or overdue flag). */
export function countOverdueReceivables(rows: UniversalBaseEntity[], now?: Date): number {
  return filterOverdueFinanceStates(rows, now).length;
}
